import React, { useState, useEffect } from 'react';
import { useRouteError } from 'react-router-dom';
import { AlertTriangle, Home, ArrowLeft, RefreshCcw, WifiOff, Lock, Database, Search, ChevronDown } from 'lucide-react';
import './errorPage.css';

const ErrorPage = () => {
  const [isHovering, setIsHovering] = useState(false);
  const [isExpanded, setIsExpanded] = useState(false);
  const [errorType, setErrorType] = useState('default');
  const error = useRouteError();

  useEffect(() => {
    let detectedErrorType = 'default';
    
    if (error instanceof Error) {
      if (error.message.includes('Failed to fetch')) {
        detectedErrorType = 'network';
      } else if (error.status) {
        detectedErrorType = error.status.toString();
      }
    } else if (typeof error === 'object' && error !== null) {
      detectedErrorType = error.status?.toString() || 'default';
    }

    setErrorType(detectedErrorType);
  }, [error]);

  const errorTypes = {
    '404': {
      icon: Search,
      title: 'Página no encontrada',
      description: 'La página que buscas no existe o fue movida a otra ubicación.',
      solution: 'Verifica la URL o navega a través de los enlaces del sitio.',
      details: 'Este error ocurre cuando intentas acceder a una URL que no existe en nuestro servidor. Esto puede suceder si la página fue eliminada, movida o nunca existió.'
    },
    '500': {
      icon: Database,
      title: 'Error interno del servidor',
      description: 'Ha ocurrido un problema en nuestros servidores.',
      solution: 'Por favor, intenta nuevamente en unos minutos.',
      details: 'Este es un error del servidor que indica un problema en nuestro sistema. Nuestro equipo técnico ha sido notificado y está trabajando en solucionarlo.'
    },
    '403': {
      icon: Lock,
      title: 'Acceso denegado',
      description: 'No tienes permisos para acceder a este recurso.',
      solution: 'Contacta al administrador si crees que esto es un error.',
      details: 'Este error de seguridad ocurre cuando intentas acceder a un recurso para el cual no tienes los permisos necesarios.'
    },
    'network': {
      icon: WifiOff,
      title: 'Error de conexión',
      description: 'No se pudo establecer conexión con el servidor.',
      solution: 'Verifica tu conexión a internet y vuelve a intentar.',
      details: 'Este error ocurre cuando hay problemas de conectividad entre tu dispositivo y nuestros servidores. Puede ser debido a problemas de internet o firewall.'
    },
    'default': {
      icon: AlertTriangle,
      title: 'Error inesperado',
      description: 'Lo sentimos, algo salió mal.',
      solution: 'Por favor, intenta nuevamente o regresa al inicio.',
      details: null
    }
  };

  const currentError = errorTypes[errorType] || errorTypes.default;
  const ErrorIcon = currentError.icon;
  const isSpecificError = errorType !== 'default' && currentError.details;

  return (
    <main className="error-page">
      <section className="error-container">
        <article className="error-content">
          {/* Icono del Error */}
          <header className="error-icon-container">
            <div className="icon-background"></div>
            <div
              className="icon-wrapper"
            >
              <ErrorIcon
                size={64}
                className="error-icon"
                onMouseEnter={() => setIsHovering(true)}
                onMouseLeave={() => setIsHovering(false)}
              />
            </div>
            {isHovering && (
              <span className="error-tooltip">¡Ups!</span>
            )}
          </header>

          {/* Contenido del Error */}
          <section className="error-message-container">
            <header className="error-header">
              <h1 className="error-title">{currentError.title}</h1>
              {errorType !== 'default' && (
                <p className="error-code">Error {errorType}</p>
              )}
            </header>

            <div className="error-description">
              <p className="description-text">{currentError.description}</p>
              <p className="solution-text">{currentError.solution}</p>
            </div>

            {/* Panel de Detalles - Solo para errores específicos */}
            {isSpecificError && (
              <details
                className="error-details"
                open={isExpanded}
              >
                <summary
                  className={`details-summary ${isExpanded ? 'expanded' : ''}`}
                  onClick={(e) => {
                    e.preventDefault();
                    setIsExpanded(!isExpanded);
                  }}
                >
                  <ErrorIcon
                    size={32}
                    className="error-icon"
                    onMouseEnter={() => setIsHovering(true)}
                    onMouseLeave={() => setIsHovering(false)}
                  />
                  <span>Más información sobre este error</span>
                  <ChevronDown className={`details-icon ${isExpanded ? 'expanded' : ''}`} />
                </summary>

                {isExpanded && (
                  <div className="details-content">
                    <p>{currentError.details}</p>
                  </div>
                )}
              </details>
            )}

            {/* Botones de Acción */}
            <nav className="error-actions">
              <button
                className="action-button back-button"
                onClick={() => window.history.back()}
                aria-label="Volver a la página anterior"
              >
                <ArrowLeft className="button-icon" aria-hidden="true" />
                <span>Volver atrás</span>
              </button>

              <button
                className="action-button home-button"
                onClick={() => window.location.href = '/'}
                aria-label="Ir a la página de inicio"
              >
                <Home className="button-icon" aria-hidden="true" />
                <span>Ir al inicio</span>
              </button>

              <button
                className="action-button reload-button"
                onClick={() => window.location.reload()}
                aria-label="Recargar la página"
              >
                <RefreshCcw className="button-icon" aria-hidden="true" />
                <span>Recargar</span>
              </button>
            </nav>
          </section>

          {/* Elementos Decorativos */}
          <div className="decorative-elements" aria-hidden="true">
            <div className="decorative-dot dot-1"></div>
            <div className="decorative-dot dot-2"></div>
          </div>
        </article>
      </section>
    </main>
  );
};

export default ErrorPage;